import { apiEmployeeTask } from "@/api/employees/employee-task.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeeTodayKeys } from "@/queries/employees/use-employee-today.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiTaskCommentFilter,
  ApiTaskCommentPayload,
  ApiTaskEmployeeRole,
  ApiTaskFilter,
  ApiTaskPayload,
} from "@/types/task.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const employeeTasksKeys = createQueryKeys("employeeTasks", {
  getMyTodo: {
    queryKey: ["my"],
    queryFn: async () => {
      const result = await apiEmployeeTask.getMyTodo();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  },
  getByEmployeeTodo: (employeeId: number, role: ApiTaskEmployeeRole) => ({
    queryKey: [employeeId, role],
    queryFn: async () => {
      const result = await apiEmployeeTask.getByEmployeeTodo(employeeId, role);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  searchMy: (filter: ApiTaskFilter) => ({
    queryKey: [filter],
    queryFn: async () => {
      const result = await apiEmployeeTask.searchMy(filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  searchByEmployee: (employeeId: number, role: ApiTaskEmployeeRole, filter: ApiTaskFilter) => ({
    queryKey: [employeeId, role, filter],
    queryFn: async () => {
      const result = await apiEmployeeTask.searchByEmployee(employeeId, role, filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  getById: (taskId: number) => ({
    queryKey: [taskId],
    queryFn: async () => {
      const result = await apiEmployeeTask.getById(taskId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  getActions: (taskId: number) => ({
    queryKey: [taskId],
    queryFn: async () => {
      const result = await apiEmployeeTask.getActions(taskId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  searchCommentsInfinite: (taskId: number, filter: ApiTaskCommentFilter) => ({
    queryKey: [taskId, filter],
  }),
});

export const useEmployeeTodoTasks = (employeeId: number, role: ApiTaskEmployeeRole) => {
  return useQuery({
    ...employeeTasksKeys.getByEmployeeTodo(employeeId, role),
    staleTime: 60 * 1000,
  });
};

export const useMyTodoTasks = (enabled = true) => {
  return useQuery({
    ...employeeTasksKeys.getMyTodo,
    staleTime: 60 * 1000,
    enabled,
  });
};

export const useMyTasksSearch = (filter: ApiTaskFilter, enabled = true) => {
  return useQuery({
    ...employeeTasksKeys.searchMy(filter),
    staleTime: 60 * 1000,
    enabled,
  });
};

export const useEmployeeTasksSearch = (
  employeeId: number,
  role: ApiTaskEmployeeRole,
  filter: ApiTaskFilter,
  enabled = true
) => {
  return useQuery({
    ...employeeTasksKeys.searchByEmployee(employeeId, role, filter),
    staleTime: 60 * 1000,
    enabled,
  });
};

export const useTask = (taskId: number | null) => {
  return useQuery({
    ...employeeTasksKeys.getById(taskId ?? 0),
    enabled: taskId !== null,
    staleTime: 60 * 1000,
  });
};

export const useTaskActions = (taskId: number) => {
  return useQuery({
    ...employeeTasksKeys.getActions(taskId),
    staleTime: 60 * 1000,
  });
};

interface TaskSaveParams {
  id?: number;
  payload: ApiTaskPayload;
}

export const useTaskSave = <T extends FieldValues>(control: Control<T> | undefined = undefined) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: TaskSaveParams) => {
      const result = id
        ? await apiEmployeeTask.update(id, payload)
        : await apiEmployeeTask.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getMyTodo._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getByEmployeeTodo._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.searchMy._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.searchByEmployee._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getById(data.id).queryKey });
      void queryClient.invalidateQueries({
        queryKey: employeeTasksKeys.getActions(data.id).queryKey,
      });
      void queryClient.invalidateQueries({ queryKey: employeeTodayKeys.getToday._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useTaskComplete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (taskId: number) => {
      const result = await apiEmployeeTask.complete(taskId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, taskId) => {
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getMyTodo._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getByEmployeeTodo._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.searchMy._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.searchByEmployee._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getById(taskId).queryKey });
      void queryClient.invalidateQueries({
        queryKey: employeeTasksKeys.getActions(taskId).queryKey,
      });
      void queryClient.invalidateQueries({ queryKey: employeeTodayKeys.getToday._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTaskUncomplete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (taskId: number) => {
      const result = await apiEmployeeTask.uncomplete(taskId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, taskId) => {
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getMyTodo._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getByEmployeeTodo._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.searchMy._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.searchByEmployee._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getById(taskId).queryKey });
      void queryClient.invalidateQueries({
        queryKey: employeeTasksKeys.getActions(taskId).queryKey,
      });
      void queryClient.invalidateQueries({ queryKey: employeeTodayKeys.getToday._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTaskStart = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (taskId: number) => {
      const result = await apiEmployeeTask.start(taskId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, taskId) => {
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getMyTodo._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getByEmployeeTodo._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.searchMy._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.searchByEmployee._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getById(taskId).queryKey });
      void queryClient.invalidateQueries({
        queryKey: employeeTasksKeys.getActions(taskId).queryKey,
      });
      void queryClient.invalidateQueries({ queryKey: employeeTodayKeys.getToday._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTaskStop = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (taskId: number) => {
      const result = await apiEmployeeTask.stop(taskId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, taskId) => {
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getMyTodo._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getByEmployeeTodo._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.searchMy._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.searchByEmployee._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getById(taskId).queryKey });
      void queryClient.invalidateQueries({
        queryKey: employeeTasksKeys.getActions(taskId).queryKey,
      });
      void queryClient.invalidateQueries({ queryKey: employeeTodayKeys.getToday._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTaskArchive = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (taskId: number) => {
      const result = await apiEmployeeTask.archive(taskId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, taskId) => {
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getMyTodo._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getByEmployeeTodo._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.searchMy._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.searchByEmployee._def });
      void queryClient.invalidateQueries({ queryKey: employeeTasksKeys.getById(taskId).queryKey });
      void queryClient.invalidateQueries({
        queryKey: employeeTasksKeys.getActions(taskId).queryKey,
      });
      void queryClient.invalidateQueries({ queryKey: employeeTodayKeys.getToday._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTaskCommentsSearchInfinite = (taskId: number, filter: ApiTaskCommentFilter) => {
  return useInfiniteQuery({
    queryKey: employeeTasksKeys.searchCommentsInfinite(taskId, filter).queryKey,
    queryFn: async ({ pageParam = 1, signal }) => {
      const result = await apiEmployeeTask.searchComments(
        taskId,
        { ...filter, page_number: pageParam },
        signal
      );
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage && lastPage.metadata.page_number < lastPage.metadata.total_pages
        ? lastPage.metadata.page_number + 1
        : undefined,
  });
};

export const useTaskCommentSave = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      taskId,
      commentId,
      payload,
    }: {
      taskId: number;
      commentId?: number | null;
      payload: ApiTaskCommentPayload;
    }) => {
      const result = !commentId
        ? await apiEmployeeTask.createComment(taskId, payload)
        : await apiEmployeeTask.updateComment(taskId, commentId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return { taskId, comment: result.data };
    },
    onSuccess: ({ taskId }) => {
      void queryClient.invalidateQueries({
        queryKey: [...employeeTasksKeys.searchCommentsInfinite._def, taskId],
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTaskCommentDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ taskId, commentId }: { taskId: number; commentId: number }) => {
      const result = await apiEmployeeTask.deleteComment(taskId, commentId);
      if (result.error) throw new ApiErrorException(result.error);
      return { taskId };
    },
    onSuccess: ({ taskId }) => {
      void queryClient.invalidateQueries({
        queryKey: [...employeeTasksKeys.searchCommentsInfinite._def, taskId],
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
